import { SelectOption } from '../ui/atoms/Select';
import { ErrorType, VariantSelectionError } from '../types/errors';
import { parseVariantMetafields, hasProductTag } from './product';
import { ProductTag } from '../types/common';

const shouldApplySoldOutRules = (product: Shopify.Product): boolean =>
  hasProductTag(product.tags, ProductTag.Limited) ||
  hasProductTag(product.tags, ProductTag.Evergreen) ||
  hasProductTag(product.tags, ProductTag.Hybrid);

const getVariantStockStatus = (
  variant: Shopify.ProductVariant,
  applySoldOutRules: boolean,
): { available: boolean; secondaryText?: string } => {
  const { quantityAvailable } = variant;
  const metafields = parseVariantMetafields(variant.metafields);
  const { lowStockThreshold } = metafields;

  // Check if variant is sold out (only for Limited, Evergreen, or Hybrid products)
  const isSoldOut =
    applySoldOutRules && (quantityAvailable === null || quantityAvailable === undefined || quantityAvailable <= 0);

  if (isSoldOut) {
    return {
      available: false,
      secondaryText: 'Sold out',
    };
  }

  // Low stock (only show if threshold is set and quantity is at or below it)
  if (lowStockThreshold && quantityAvailable && quantityAvailable <= lowStockThreshold) {
    return {
      available: true,
      secondaryText: `${quantityAvailable} in stock`,
    };
  }

  // Normal stock
  return {
    available: true,
  };
};

const createSelectOption = (variant: Shopify.ProductVariant, applySoldOutRules: boolean): SelectOption => {
  const stockStatus = getVariantStockStatus(variant, applySoldOutRules);
  return {
    id: variant.id,
    label: variant.title,
    value: variant.id,
    ...stockStatus,
  };
};

export const variantsToSelectOptions = (
  variants: Shopify.ProductVariantEdge[],
  product?: Shopify.Product,
): SelectOption[] => {
  // Check once if this product type should respect sold-out states
  const applySoldOutRules = product ? shouldApplySoldOutRules(product) : false;

  return variants.map((edge) => createSelectOption(edge.node, applySoldOutRules));
};

export const getSelectedSizeForProduct = (
  product: Shopify.Product,
  selectedVariants: Shopify.ProductVariant[],
): SelectOption | undefined => {
  const selectedVariant = selectedVariants.find((v) => v.product.id === product.id);
  if (selectedVariant) {
    const applySoldOutRules = shouldApplySoldOutRules(product);
    return createSelectOption(selectedVariant, applySoldOutRules);
  }
  return undefined;
};

export const variantToSelectOption = (
  variant?: Shopify.ProductVariant,
  product?: Shopify.Product,
): SelectOption | undefined => {
  if (!variant) return undefined;
  const applySoldOutRules = product ? shouldApplySoldOutRules(product) : false;
  return createSelectOption(variant, applySoldOutRules);
};

export const hasApparelSizeError = (productId: string, errors: VariantSelectionError[]): boolean =>
  errors.some((error) => error.productID === productId && error.error === ErrorType.ApparelSizeMissing);

export const findVariantBySelectOption = (
  variants: Shopify.ProductVariantEdge[],
  selectedOption: SelectOption,
): Shopify.ProductVariant | undefined => variants.find((edge) => edge.node.id === selectedOption.id)?.node;
